import { Hub } from "aws-amplify/utils";
import {
  signIn as ampSignIn,
  signOut as ampSignOut,
  confirmSignIn as ampConfirmSignIn,
  resetPassword as ampResetPassword,
  confirmResetPassword as ampConfirmResetPassword,
  fetchAuthSession,
  getCurrentUser,
  fetchUserAttributes,
  fetchMFAPreference,
  setUpTOTP as ampSetUpTOTP,
  verifyTOTPSetup as ampVerifyTOTPSetup,
  updateMFAPreference as ampUpdateMFAPreference,
} from "aws-amplify/auth";
import type { User } from "@observe-tech/mfe-shell-api";
import type { OfflineSnapshot } from "@observe-tech/utils";
import { isSnapshotUsable } from "./offlineSession";
import { clearSyncedTokens, syncedTokenSub } from "./syncedTokens";
import { broadcastAuthToOtherScreens } from "./desktopAuthSync";
import {
  getEmergencyRole,
  type EmergencyRole,
} from "../exported/shared/utils/emergencySession";
import { createEmergencyAuthApi } from "./emergencyAuth";
import { clearAmplifyKeysLocally } from "./amplifyKeys";
import { isQuickLoginCapable, listQuickLogins } from "./quickLoginSession";
import { getOfflineMarker } from "./offlineMarker";

export interface SignInResult {
  isSignedIn: boolean;
  nextStep?: { signInStep: string; totpSecret?: string };
}

export interface ResetPasswordResult {
  isPasswordReset: boolean;
  nextStep?: { resetPasswordStep: string };
}

export interface SessionExpiredInfo {
  // true when the user asked to sign out; false for a token/session that died
  // on its own. Only the latter may leave a returnTo behind.
  deliberate: boolean;
  // true when cognito itself refused the refresh token: the account was
  // disabled or signed out everywhere, not merely a token that ran down here.
  refused?: boolean;
}

// The watcher's offline half is injected rather than imported: connectivity is
// a live poller owned by whoever starts the watcher, and the snapshot lives
// behind IPC that only the desktop build has. Supplying neither is today's
// behaviour, exactly.
export interface TokenWatcherOptions {
  intervalMs?: number;
  probeOnline?: () => Promise<boolean>;
  readSnapshot?: () => Promise<OfflineSnapshot | null>;
  now?: () => number;
}

export interface AuthApi {
  readonly emergencyRole: EmergencyRole | null;
  getToken(opts?: { forceRefresh?: boolean }): Promise<string>;
  signIn(username: string, password: string): Promise<SignInResult>;
  confirmSignIn(challengeResponse: string): Promise<SignInResult>;
  isTotpEnrolmentRequired(): Promise<boolean>;
  startTotpSetup(): Promise<string>;
  completeTotpSetup(code: string): Promise<void>;
  abandonSignIn(): Promise<void>;
  resetPassword(username: string): Promise<ResetPasswordResult>;
  confirmResetPassword(
    username: string,
    confirmationCode: string,
    newPassword: string,
  ): Promise<void>;
  signOut(opts?: { deliberate?: boolean }): Promise<void>;
  loadCurrentUser(): Promise<User>;
  getAuthTimeMs(): Promise<number | null>;
  onTokenRefresh(cb: (token: string) => void): () => void;
  onSessionExpired(cb: (info: SessionExpiredInfo) => void): () => void;
  // Fires once when an offline session reaches the expiry its snapshot
  // recorded. This, and nothing else, ends a session that is running offline.
  onOfflineExpiry(cb: () => void): () => void;
  // The other cause with the same consequence: the link came back and cognito
  // refused the refresh, so the enrolment is spent rather than merely
  // unreachable. Offline the two are indistinguishable, which is why only a
  // reconnect can report this one.
  endOfflineSession(): void;
  startTokenWatcher(options?: TokenWatcherOptions): () => void;
  stopTokenWatcher(): void;
}

export interface CognitoAuthApi extends AuthApi {
  dropLingeringSession(): Promise<void>;
}

const REFRESH_LEAD_MS = 5 * 60 * 1000;

function toSignInResult(r: {
  isSignedIn: boolean;
  nextStep?: {
    signInStep: string;
    totpSetupDetails?: { sharedSecret: string };
  };
}): SignInResult {
  if (!r.nextStep) return { isSignedIn: r.isSignedIn };
  const totpSecret = r.nextStep.totpSetupDetails?.sharedSecret;
  return {
    isSignedIn: r.isSignedIn,
    nextStep: {
      signInStep: r.nextStep.signInStep,
      ...(totpSecret ? { totpSecret } : {}),
    },
  };
}

let cached: AuthApi | null = null;
let cachedCognito: CognitoAuthApi | null = null;

export function createAuthApi(): AuthApi {
  if (cached) return cached;
  const role = getEmergencyRole();
  cached = role ? createEmergencyAuthApi(role) : createCognitoAuthApi();
  return cached;
}

export function createCognitoAuthApi(): CognitoAuthApi {
  if (cachedCognito) return cachedCognito;

  const tokenSubs = new Set<(t: string) => void>();
  const expiredSubs = new Set<(info: SessionExpiredInfo) => void>();
  const offlineExpirySubs = new Set<() => void>();

  // Amplify reports every sign-out as the same "signedOut" Hub event, so the
  // reason has to be recorded here, at the call site, before ampSignOut runs.
  // "housekeeping" is signIn's pre-emptive sign-out: it is neither an expiry
  // nor a deliberate exit, and must not disturb a pending returnTo.
  let pendingSignOut: "user" | "housekeeping" | null = null;

  function emitToken(t: string) {
    for (const s of tokenSubs) {
      try {
        s(t);
      } catch {
        /* swallow subscriber errors */
      }
    }
  }

  function emitExpired(info: SessionExpiredInfo) {
    for (const s of expiredSubs) {
      try {
        s(info);
      } catch {
        /* swallow */
      }
    }
  }

  function emitOfflineExpiry() {
    for (const s of offlineExpirySubs) {
      try {
        s();
      } catch {
        /* swallow */
      }
    }
  }

  Hub.listen("auth", ({ payload }) => {
    switch (payload.event) {
      case "tokenRefresh":
        void fetchAuthSession()
          .then((s) => {
            const t = s.tokens?.idToken?.toString();
            if (t) emitToken(t);
            else emitExpired({ deliberate: false });
          })
          .catch(() => emitExpired({ deliberate: false }));
        break;
      case "tokenRefresh_failure": {
        // Amplify dispatches this for every failed refresh, including one that
        // never reached cognito, and dispatches it before the caller's
        // fetchAuthSession rejects. Reporting that as an expiry sends an
        // offline session to /login before the boot or the watcher can ask
        // the probe; the caller's own catch owns the network case.
        const error = (payload as { data?: { error?: { name?: string } } }).data
          ?.error;
        if (error?.name === "NetworkError") break;
        // The one place every refusal passes through: amplify resolves the
        // caller's fetchAuthSession with no tokens rather than rejecting it,
        // so the boot, the watcher and the reconnect all learn of it only here.
        if (error?.name?.startsWith("NotAuthorizedException")) {
          emitExpired({ deliberate: false, refused: true });
          break;
        }
        emitExpired({ deliberate: false });
        break;
      }
      case "signedOut": {
        const reason = pendingSignOut;
        pendingSignOut = null;
        if (reason === "housekeeping") break;
        emitExpired({ deliberate: reason === "user" });
        break;
      }
      case "signedIn":
        void fetchAuthSession()
          .then((s) => {
            const t = s.tokens?.idToken?.toString();
            if (t) emitToken(t);
          })
          .catch(() => {});
        break;
    }
  });

  async function currentUserHasPin(): Promise<boolean> {
    const sub = syncedTokenSub() ?? getOfflineMarker();
    if (!sub || !isQuickLoginCapable()) return false;
    return (await listQuickLogins()).some(
      (r) => r.hasPin && r.cognitoSub === sub,
    );
  }

  async function housekeepingSignOut(): Promise<void> {
    if (await currentUserHasPin()) {
      await clearAmplifyKeysLocally();
      return;
    }
    pendingSignOut = "housekeeping";
    try {
      await ampSignOut();
    } catch {
      /* no existing session */
    } finally {
      pendingSignOut = null;
    }
  }

  let watcher: ReturnType<typeof setInterval> | null = null;
  // One expiry per offline session, not one per 60s tick.
  let offlineExpiryEmitted = false;

  const api: CognitoAuthApi = {
    emergencyRole: null,
    async getToken({ forceRefresh = false } = {}) {
      const session = await fetchAuthSession({ forceRefresh });
      const t = session.tokens?.idToken;
      if (!t) throw new Error("No session");
      return t.toString();
    },
    // Drop any lingering Cognito session so a stale/partial sign-in (e.g. a
    // previous user that was not fully signed out) does not block this one.
    async dropLingeringSession() {
      await housekeepingSignOut();
      clearSyncedTokens();
    },
    async signIn(username, password) {
      await api.dropLingeringSession();
      return toSignInResult(await ampSignIn({ username, password }));
    },
    async confirmSignIn(challengeResponse) {
      return toSignInResult(await ampConfirmSignIn({ challengeResponse }));
    },
    async isTotpEnrolmentRequired() {
      const attributes = await fetchUserAttributes();
      if (attributes["custom:mfa"] !== "true") return false;
      const { enabled } = await fetchMFAPreference();
      return !enabled?.includes("TOTP");
    },
    async startTotpSetup() {
      const { sharedSecret } = await ampSetUpTOTP();
      return sharedSecret;
    },
    async completeTotpSetup(code) {
      await ampVerifyTOTPSetup({ code });
      await ampUpdateMFAPreference({ totp: "PREFERRED" });
    },
    async abandonSignIn() {
      try {
        await housekeepingSignOut();
      } finally {
        clearSyncedTokens();
      }
    },
    async resetPassword(username) {
      const r = await ampResetPassword({ username });
      return { isPasswordReset: r.isPasswordReset, nextStep: r.nextStep };
    },
    async confirmResetPassword(username, confirmationCode, newPassword) {
      await ampConfirmResetPassword({
        username,
        confirmationCode,
        newPassword,
      });
    },
    async signOut({ deliberate = true } = {}) {
      pendingSignOut = deliberate ? "user" : null;
      try {
        if (await currentUserHasPin()) await clearAmplifyKeysLocally();
        else await ampSignOut();
      } finally {
        pendingSignOut = null;
        clearSyncedTokens();
        emitExpired({ deliberate });
        // Other screens have no other way to learn this screen just signed
        // out — see main.tsx's onAuthBroadcast wiring.
        void broadcastAuthToOtherScreens();
      }
    },
    async loadCurrentUser() {
      const u = await getCurrentUser();
      const email = u.signInDetails?.loginId ?? "";
      return {
        id: u.userId,
        cognitoSub: u.userId,
        email,
        name: email,
      };
    },
    // The moment the user actually authenticated, not the moment this token was
    // issued: Cognito carries auth_time through every refresh and only moves it
    // on a real sign-in. Anything anchored on Date.now() instead slides forward
    // on every boot, because the callers below run on every boot.
    // A dead session cannot say when it began, so it answers null like a token
    // without the claim does. Callers use this to decide whether they may write
    // something long-lived; none of them wants that decision to throw.
    async getAuthTimeMs() {
      try {
        const session = await fetchAuthSession();
        const id = session.tokens?.idToken;
        const authTime = (id?.payload as { auth_time?: number } | undefined)
          ?.auth_time;
        return typeof authTime === "number" ? authTime * 1000 : null;
      } catch {
        return null;
      }
    },
    onTokenRefresh(cb) {
      tokenSubs.add(cb);
      return () => {
        tokenSubs.delete(cb);
      };
    },
    onSessionExpired(cb) {
      expiredSubs.add(cb);
      return () => {
        expiredSubs.delete(cb);
      };
    },
    onOfflineExpiry(cb) {
      offlineExpirySubs.add(cb);
      return () => {
        offlineExpirySubs.delete(cb);
      };
    },
    endOfflineSession() {
      if (offlineExpiryEmitted) return;
      offlineExpiryEmitted = true;
      emitOfflineExpiry();
    },
    startTokenWatcher(options: TokenWatcherOptions = {}) {
      const { intervalMs = 60_000, probeOnline, readSnapshot } = options;
      if (watcher) clearInterval(watcher);
      offlineExpiryEmitted = false;

      // A failed tick is two different events wearing one face. Cognito saying
      // no is an expiry; Cognito being unreachable is a network outage, and
      // treating it as an expiry is what logs an online user out on every blip
      // and would log an offline user out immediately. The probe is the only
      // thing that can tell them apart, so it runs before any conclusion.
      const handledOffline = async (): Promise<boolean> => {
        if (!probeOnline || !readSnapshot) return false;
        if (await probeOnline()) return false;
        const snapshot = await readSnapshot();
        const now = options.now?.() ?? Date.now();
        if (snapshot && isSnapshotUsable(snapshot, now)) return true;
        if (!offlineExpiryEmitted) {
          offlineExpiryEmitted = true;
          emitOfflineExpiry();
        }
        return true;
      };

      const tick = async () => {
        try {
          const session = await fetchAuthSession();
          const id = session.tokens?.idToken;
          if (!id) {
            emitExpired({ deliberate: false });
            return;
          }
          const exp = (id.payload as { exp?: number } | undefined)?.exp;
          if (typeof exp === "number") {
            const msToExpiry = exp * 1000 - Date.now();
            if (msToExpiry < REFRESH_LEAD_MS) {
              const refreshed = await fetchAuthSession({ forceRefresh: true });
              const newT = refreshed.tokens?.idToken?.toString();
              if (newT) emitToken(newT);
              else emitExpired({ deliberate: false });
            }
          }
        } catch {
          if (await handledOffline()) return;
          emitExpired({ deliberate: false });
        }
      };
      void tick();
      watcher = setInterval(() => void tick(), intervalMs);
      return () => api.stopTokenWatcher();
    },
    stopTokenWatcher() {
      if (watcher) clearInterval(watcher);
      watcher = null;
    },
  };

  cachedCognito = api;
  return api;
}
